import React from 'react';
import { Redirect, useLocation } from 'react-router-dom';

import { AUTHENTICATION_STATES } from '../../shared/constants/auth';
import { useAppContext } from '../../shared/contexts/AppContext';

/**
 * A component decorator that will handle checking for a currentUser,
 * but does not require logging-in to view the content.
 *
 * export default authOptional(MyExampleComponent);
 *
 * NOTE: The decorated component will still render even if the user has not yet authed, this happens
 * because auth happens asynchronously so there is a period of time when don't know whether or
 * not the user is authenticated.
 *
 * Your component should still check for the presence of
 * currentUser if it actually needs to use the user data.
 *
 * @param   {React.Component} DecoratedComponent The component that requires auth.
 * @returns {React.Component} A wrapper component that will perform the auth check.
 */
export default function AuthOptional(DecoratedComponent) {

	const UserAuthWrapper = (props) => {

		const { currentUser, authState, membership, myOrganization } = useAppContext();
		const { pathname, search } = useLocation();

		// If authentication check has not completed yet, just show an empty div...
		if (authState === AUTHENTICATION_STATES.PENDING ||
			authState === AUTHENTICATION_STATES.UNINITIALIZED) {
			return <div></div>;
		}

		if (authState === AUTHENTICATION_STATES.AUTHENTICATED && currentUser && myOrganization) {

			// Check if current user still needs to agree to the TechChange terms of service
			if (!currentUser.terms_of_use || !currentUser.privacy_policy) {

				// NOTE: prevent infinite looping
				if (pathname !== '/register/terms-of-service' && pathname.indexOf('/policies') !== 0) {

					const redirectParam = encodeURIComponent(`${pathname}${search}`);

					return <Redirect to={`/register/terms-of-service?redirect=${redirectParam}`} />;

				}

			}

			// Check if current user still needs to agree to the Organization's terms of service
			if (membership &&
				((myOrganization.terms_of_use_url && !membership.terms_of_use) ||
				(myOrganization.privacy_policy_url && !membership.privacy_policy))) {

				// NOTE: prevent infinite looping
				if (pathname !== '/register/terms-of-service' && pathname.indexOf('/policies') !== 0) {

					const redirectParam = encodeURIComponent(`${pathname}${search}`);

					return <Redirect to={`/register/terms-of-service?redirect=${redirectParam}`} />;

				}

			}

		}

		// Show the component as soon as the check has completed, regardless of logged-in status!
		return <DecoratedComponent {...props} />;

	};

	return UserAuthWrapper;

}
